import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api } from '../api/client';
import { billingEnabled } from '../config/features';
import { useAuth } from './AuthContext';

const DEFAULT_STATE = {
  isPro: false,
  status: 'inactive',
  plan: null,
  currentPeriodEnd: null,
  loading: false,
};

// Default-safe: components can call useSubscription() even without a provider
// (e.g. in isolated tests) and simply get the inactive defaults.
const SubscriptionContext = createContext({ ...DEFAULT_STATE, refresh: () => {} });

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(DEFAULT_STATE);

  const refresh = useCallback(async () => {
    if (!billingEnabled || !user) {
      setState(DEFAULT_STATE);
      return;
    }
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const data = await api.billingStatus();
      setState({
        isPro: Boolean(data?.active),
        status: data?.status || 'inactive',
        plan: data?.plan || null,
        currentPeriodEnd: data?.current_period_end || null,
        loading: false,
      });
    } catch {
      setState(DEFAULT_STATE);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);

  return (
    <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
